import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Preferences } from '@capacitor/preferences';
import { getGeoIp } from '@/lib/utils';

interface LocationData {
  latitude: number;
  longitude: number;
  timestamp: number;
}

/**
 * Get the user's current geolocation with caching
 * If a cached location is available and less than 15 minutes old, it will be returned
 * Otherwise, a new location will be requested and cached
 */
export const getGeolocationWithCache = async (): Promise<{ latitude: number; longitude: number }> => {
  try {
    // Check if we have a cached location
    const cachedLocationJson = await Preferences.get({ key: 'cached_location' });

    if (cachedLocationJson.value) {
      const cachedLocation: LocationData = JSON.parse(cachedLocationJson.value);
      const now = Date.now();
      const fifteenMinutesInMs = 15 * 60 * 1000;

      // If the cached location is less than 15 minutes old, use it
      if (now - cachedLocation.timestamp < fifteenMinutesInMs) {
        console.log('Using cached location');
        return {
          latitude: cachedLocation.latitude,
          longitude: cachedLocation.longitude
        };
      }
    }

    // No valid cached location, get a new one
    try {
      try {
        // Check if we have permission to access location
        const permissionStatus = await Geolocation.checkPermissions();

        if (permissionStatus.location !== 'granted') {
          // Request permission if not granted
          const requestResult = await Geolocation.requestPermissions();

          if (requestResult.location !== 'granted') {
            throw new Error('Location permission not granted');
          }
        }

        // If we're on a native platform, use Capacitor's Geolocation
        if (Capacitor.isNativePlatform()) {
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          });

          // Cache the new location
          const locationData: LocationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: Date.now()
          };

          await Preferences.set({
            key: 'cached_location',
            value: JSON.stringify(locationData)
          });

          return {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
        }
        // Otherwise use the Web Geolocation API
        else {
          console.log('Getting location on web platform');

          return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              ({ coords }) => {
                console.log('Web location found:', coords.latitude, coords.longitude);

                // Cache the new location
                const locationData: LocationData = {
                  latitude: coords.latitude,
                  longitude: coords.longitude,
                  timestamp: Date.now()
                };

                Preferences.set({
                  key: 'cached_location',
                  value: JSON.stringify(locationData)
                }).then(() => {
                  resolve({ latitude: coords.latitude, longitude: coords.longitude });
                });
              },
              (err) => {
                // Fallback to IP-based geolocation
                getGeoIp().then(ipLocation => {
                  // Cache the IP-based location
                  const locationData: LocationData = {
                    latitude: ipLocation.lat,
                    longitude: ipLocation.lon,
                    timestamp: Date.now()
                  };

                  Preferences.set({
                    key: 'cached_location',
                    value: JSON.stringify(locationData)
                  }).then(() => {
                    resolve({ latitude: ipLocation.lat, longitude: ipLocation.lon });
                  });
                }).catch(ipError => {
                  reject(new Error('All geolocation methods failed'));
                });
              }
            );
          });
        }
      } catch (deviceError) {
        console.error('Device geolocation error:', deviceError);

        // Fallback to IP-based geolocation if device geolocation fails
        const ipLocation = await getGeoIp();

        // Cache the IP-based location
        const locationData: LocationData = {
          latitude: ipLocation.lat,
          longitude: ipLocation.lon,
          timestamp: Date.now()
        };

        await Preferences.set({
          key: 'cached_location',
          value: JSON.stringify(locationData)
        });

        return {
          latitude: ipLocation.lat,
          longitude: ipLocation.lon
        };
      }
    } catch (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
};
